import {createContext, FunctionComponent, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import notebookTags, {NotebookTag} from "../../../core/models/static/routes/notebook-tags";
import {useLocation} from "react-router-dom";

type ArrayElementType<T> = T extends readonly (infer ElementType)[] ? ElementType : never


interface NotebookConfigState {
    show: boolean,
    fabNotesVisible: boolean,
    tasksConfig: {
        tagGroups?: Array<NotebookTag[]>,
        onTaskUpsert?: (task: any, isDeleted?: boolean) => void,
    },
    notesConfig: {
        tagGroups?: Array<NotebookTag[]>,
        onNoteUpsert?: (note: any, isDeleted?: boolean) => void,
    },
}

const defaultNotebookConfigState: NotebookConfigState = {
    show: true,
    fabNotesVisible: true,
    tasksConfig: {
        tagGroups: [notebookTags.default],
        onTaskUpsert: () => void 0,
    },
    notesConfig: {
        tagGroups: [notebookTags.default],
        onNoteUpsert: () => void 0,
    },
}


const NotebookConfigStateContext = createContext<NotebookConfigState>(defaultNotebookConfigState)

type NotebookTags = typeof notebookTags
export type NoteTagsConfigurator = (container: NotebookTags) => ArrayElementType<Required<NotebookConfigState['notesConfig']>['tagGroups']>;

interface ConfigureNotebookProviderProps {
    configureNotebookTags: (...configurators: Array<NoteTagsConfigurator>) => void;
    assignOnNoteUpsert: (callback: (note: any, isDeleted?: boolean) => void) => void;
    assignOnTaskUpsert: (callback: (task: any, isDeleted?: boolean) => void) => void;
    toggleShow: (name: string, show: boolean) => void;
    toggleFabNotes: (name: string, show: boolean) => void;
}


const defaultConfigureNotebookProviderProps: ConfigureNotebookProviderProps = {
    configureNotebookTags: () => void 0,
    assignOnNoteUpsert: () => () => void 0,
    assignOnTaskUpsert: () => () => void 0,
    toggleShow: () => void 0,
    toggleFabNotes: () => void 0,
}

export const ConfigureNotebookProviderContext = createContext<ConfigureNotebookProviderProps>(defaultConfigureNotebookProviderProps);

interface ShowRecord {
    component: string,
    show: boolean,
}

/**
 * Provider to configure the notebook. This provider should be used at the root of the application
 */
export const NotebookConfigProvider: FunctionComponent<PropsWithChildren<{}>> = (props) => {
    const [_configState, setConfigState] = useState<Omit<NotebookConfigState, 'show'>>(defaultNotebookConfigState);
    const [showRecords, setShowRecords] = useState<Record<ShowRecord['component'], ShowRecord>>({});
    const [fabNotesRecords, setFabNotesRecords] = useState<Record<ShowRecord['component'], ShowRecord>>({});

    /**
     * Configure the note tags to be used in the notebook
     */
    const configureNotebookTags = useCallback<ConfigureNotebookProviderProps['configureNotebookTags']>((...configurators) => {
        setConfigState(prevState => ({
            ...prevState,
            notesConfig: {
                ...prevState.notesConfig,
                tagGroups: configurators.map(configurator => configurator(notebookTags)),
            },
            tasksConfig: {
                ...prevState.tasksConfig,
                tagGroups: configurators.map(configurator => configurator(notebookTags)),
            }
        }))
    }, [])

    /**
     * Toggle the show state of the notebook
     */
    const toggleShow = useCallback<ConfigureNotebookProviderProps['toggleShow']>((name, show) => {
        setShowRecords(prevState => ({
            ...prevState,
            [name]: {
                component: name,
                show,
            },
        }))
    }, [])

    /**
     * Toggle FAB notes tab visibility for a registered view
     */
    const toggleFabNotes = useCallback<ConfigureNotebookProviderProps['toggleFabNotes']>((name, show) => {
        setFabNotesRecords(prevState => ({
            ...prevState,
            [name]: {
                component: name,
                show,
            },
        }))
    }, [])

    /**
     * Assign the onNoteUpsert function to the notebook configuration state
     */
    const assignOnNoteUpsert = useCallback<(callback: Required<NotebookConfigState['notesConfig']>['onNoteUpsert']) => void>((callback) => {
        setConfigState(prevState => ({
            ...prevState,
            notesConfig: {
                ...prevState.notesConfig,
                onNoteUpsert: callback,
            }
        }))
    }, [])

    /**
     * Assign the onTaskUpsert function to the notebook configuration state
     */
    const assignOnTaskUpsert = useCallback<(callback: Required<NotebookConfigState['tasksConfig']>['onTaskUpsert']) => void>((callback) => {
        setConfigState(prevState => ({
            ...prevState,
            tasksConfig: {
                ...prevState.tasksConfig,
                onTaskUpsert: callback,
            }
        }))
    }, [])

    const configureNotebookState = useMemo<ConfigureNotebookProviderProps>(() => ({
        configureNotebookTags,
        assignOnNoteUpsert,
        assignOnTaskUpsert,
        toggleShow,
        toggleFabNotes,
    }), [assignOnNoteUpsert, assignOnTaskUpsert, configureNotebookTags, toggleFabNotes, toggleShow])

    const show = useMemo(() => {
        const list = Object.values(showRecords);
        return !list.length || !list.some(({show}) => !show)
    }, [showRecords])

    const fabNotesVisible = useMemo(() => {
        const list = Object.values(fabNotesRecords);
        return !list.length || !list.some(({show}) => !show)
    }, [fabNotesRecords])

    const configState = useMemo<NotebookConfigState>(() => ({
        ..._configState,
        show: show,
        fabNotesVisible: fabNotesVisible,
    }), [_configState, fabNotesVisible, show])

    return (
        <>
            <ConfigureNotebookProviderContext.Provider value={configureNotebookState}>
                <NotebookConfigStateContext.Provider value={configState}>
                    {props.children}
                </NotebookConfigStateContext.Provider>
            </ConfigureNotebookProviderContext.Provider>
        </>
    );
}


interface UseConfigureNotebookResult extends Omit<ConfigureNotebookProviderProps, 'toggleShow' | 'toggleFabNotes'> {
    toggleShow: (show: boolean) => void;
    toggleFabNotes: (show: boolean) => void;
}

/**
 * Hook to get the configuration state of the notebook
 */
export const useConfigureNotebook = (name?: string): UseConfigureNotebookResult => {
    const state = useContext(ConfigureNotebookProviderContext);
    const {configureNotebookTags, toggleShow, toggleFabNotes, assignOnTaskUpsert, assignOnNoteUpsert} = state;
    const location = useLocation();
    const defaultNameRef = useRef(location.pathname);
    defaultNameRef.current = location.pathname;

    const showName = useMemo(() => name ?? defaultNameRef.current, [name]);

    /**
     * As soon as the component is unmounted:
     * - Reset the note configuration state
     */
    useEffect(() => () => {
        configureNotebookTags((noteTags) => noteTags.default);
    }, [configureNotebookTags]);
    /**
     * As soon as the component is unmounted:
     * - Reset the show state
     */
    useEffect(() => () => {
        toggleShow(showName, true);
    }, [showName, toggleShow]);
    /**
     * As soon as the component is unmounted:
     * - Reset FAB notes visibility
     */
    useEffect(() => () => {
        toggleFabNotes(showName, true);
    }, [showName, toggleFabNotes]);

    return useMemo(() => ({
        configureNotebookTags,
        assignOnTaskUpsert,
        assignOnNoteUpsert,
        toggleShow: (show) => toggleShow(showName, show),
        toggleFabNotes: (show) => toggleFabNotes(showName, show),
    }), [configureNotebookTags, assignOnTaskUpsert, assignOnNoteUpsert, toggleFabNotes, toggleShow, showName]);
}

/**
 * Hook to get the configuration state of the notebook
 */
export const useNotebookConfig = (): NotebookConfigState => {
    return useContext(NotebookConfigStateContext);
}
